import {
  useGetTechnicalMessageDetailsQuery,
  useHideTechnicalMessageMutation,
} from '@api/technicalMessage.api';
import { TextCodes } from '@api/types/translation/TextCodes';
import { BackButton, BlockContainer, DocumentList, Html, LoadingError, Title } from '@components';
import { useAppSelector, useAuth } from '@hooks';
import {
  Box,
  Divider,
  Grid,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { getSpecialPage } from '@store/app/app.slice';
import { colors } from '@themes/colors';
import { t } from '@translations';
import { longDate } from '@utils/date.utils';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

type TechnicalMessageDetailsBlockProps = {
  title?: string;
};

export const TechnicalMessageDetailsBlock = ({ title }: TechnicalMessageDetailsBlockProps) => {
  const theme = useTheme();
  const location = useLocation();
  const { pathname } = location;
  const detailpathname = useAppSelector(getSpecialPage('technicalMessageDetailsPage')) as string;

  const urlParams = new URLSearchParams(location.search);
  const id = urlParams.get('id');
  const { currentData, isFetching, isLoading, isError } = useGetTechnicalMessageDetailsQuery(
    id || '',
    {
      skip: !id,
    }
  );
  const { isDealerUser, isBeremaUser, isOEMUser, isServiceUser } = useAuth();
  const isCustomer = isDealerUser || isOEMUser || isServiceUser;
  const [trigger] = useHideTechnicalMessageMutation();
  const isBreakepointMobile = useMediaQuery(theme.breakpoints.down('lg'));

  useEffect(() => {
    if (currentData?.data?.isNew && isCustomer) {
      const readTechnicalMessagesIds = JSON.parse(
        localStorage.getItem('readTechnicalMessagesIds') || '[]'
      );
      const filteredIds = readTechnicalMessagesIds.filter(
        (id: number) => id !== currentData?.data?.id
      );
      localStorage.setItem('readTechnicalMessagesIds', JSON.stringify(filteredIds));
      trigger(currentData?.data?.id);
    }
  }, [currentData?.data?.id, currentData?.data?.isNew, trigger, isCustomer]);

  const isLoadingError = (isError || !id) && pathname === detailpathname;
  const isWorking = isLoading || isFetching;
  const dealerTechnicalMessageListPage = useAppSelector(
    getSpecialPage('dealerTechnicalMessageListPage')
  );
  const beremaTechnicalMessageListPage = useAppSelector(
    getSpecialPage('beremaTechnicalMessageListPage')
  );
  const technicalMessageListPage = isBeremaUser
    ? beremaTechnicalMessageListPage
    : dealerTechnicalMessageListPage;

  if (isWorking) {
    return <TechnicalMessagePlaceholder />;
  }

  return (
    <BlockContainer>
      <BackButton href={technicalMessageListPage} />
      <Title variant='h2' sx={{ mb: 4 }}>
        {title || t(TextCodes.TechnicalMessage.Block.TechnicalMessage)}
      </Title>
      {isLoadingError ? (
        <LoadingError />
      ) : (
        <>
          <Title variant='h3' noDot>
            {currentData?.data?.title || ''}
          </Title>
          <Divider sx={{ mb: 2, mt: 1 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Stack direction='row' mb={2} spacing={8}>
                <div>
                  <Typography color={colors.text.secondary} fontWeight='bold !important'>
                    {t(TextCodes.TechnicalMessage.Block.Date)}:
                  </Typography>
                  <Typography variant='body2'>{longDate(currentData?.data?.date)}</Typography>
                </div>
                <div>
                  <Typography color={colors.text.secondary} fontWeight='bold !important'>
                    {t(TextCodes.TechnicalMessage.Block.MessageType)}:
                  </Typography>
                  <Typography variant='body2'>{currentData?.data?.messageType}</Typography>
                </div>
              </Stack>

              <Box>
                <Typography color={colors.text.secondary} fontWeight='bold !important'>
                  {t(TextCodes.TechnicalMessage.Block.Description)}:
                </Typography>
                <Html html={currentData?.data?.text || ''} />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box height='100%'>
                <Typography color={colors.text.secondary} fontWeight='bold !important'>
                  {t(TextCodes.TechnicalMessage.Block.Attachments)}:
                </Typography>
                {currentData?.data?.documents && (
                  <DocumentList
                    showAlert
                    documents={currentData?.data?.documents}
                    isWorking={isWorking}
                    nogrid={isBreakepointMobile ? 'true' : undefined}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </>
      )}
    </BlockContainer>
  );
};

const TechnicalMessagePlaceholder = () => {
  return (
    <BlockContainer>
      <Skeleton variant='text' width='60%' height={70} sx={{ mb: 1 }} />
      <Skeleton variant='text' width='40%' height={40} />
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Stack direction='row' mb={2} spacing={8}>
            <Box width='100%'>
              <Skeleton variant='text' width='30%' height={30} />
              <Skeleton variant='text' width='70%' height={30} />
            </Box>
            <Box width='100%'>
              <Skeleton variant='text' width='50%' height={30} />
              <Skeleton variant='text' width='70%' height={30} />
            </Box>
          </Stack>
          <Box>
            <Skeleton variant='text' width='40%' height={30} />
            <Skeleton variant='rectangular' width='100%' height={60} />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box height='100%'>
            <Skeleton variant='text' width='50%' height={30} />
            <Skeleton variant='rectangular' width='100%' height={134} />
          </Box>
        </Grid>
      </Grid>
    </BlockContainer>
  );
};
